
import { mapState } from 'vuex';
import cartMixin from '@/mixins/cartMixin.js';
import addressMixin from '@/mixins/addressMixin.js';
import { Checkbox } from 'element-ui';
import CustomerService from '@/services/CustomerService';

export default {
  mixins: [cartMixin, addressMixin],
  name: 'CheckoutCustomerGuest',
  data() {
    return {
      email: '',
      showemail: false,
      guestEmailValidationError: '',
      guestEmailAvailabilityError: '',
      newsletterSubscription: false,
      showLoginLink: false,
      guestPreRegistered: false,
      guestPreRegistering: false,
      customerService: new CustomerService({
        http: this.$http,
        auth: this.$auth,
        store: this.$store,
      }),
    };
  },
  computed: {
    ...mapState('checkout', [
      'guestInfo',
      'shippingAddress',
      'invoiceAddress',
      'sameAsDelivery',
      'shippingType',
      'checkoutCarrier',
      'checkoutRelayPoint',
    ]),
    displayedGuestEmailError() {
      return this.guestEmailValidationError || this.guestEmailAvailabilityError;
    },
    isExpressCheckoutEnabled() {
      const flag = String(process.env.EXPRESS_CHECKOUT || 'enabled').toLowerCase();
      return !['0', 'false', 'off', 'disabled', 'no'].includes(flag);
    },
    guestUser() {
      return this.$store.state.user.guest.guestUser;
    },
    isGuestAlreadyRegistered() {
      return Boolean(this.$auth.loggedIn || this.guestUser);
    },
    isGuestFormReady() {
      if (this.isGuestAlreadyRegistered) return false;
      const addr = this.shippingAddress;
      return Boolean(
        this.isValidEmail(this.email) &&
        addr?.Firstname &&
        addr?.Lastname &&
        addr?.Address1 &&
        addr?.Postcode &&
        addr?.City &&
        addr?.CountryIsoCode &&
        addr?.MobilePhone
      );
    },
  },
  mounted() {
    this.loadFromStorage();
    this.$root.$on('checkout:validate', this.validateGuestEmailForCheckout);
    this.$root.$on('checkout:guest-email-error', this.setGuestEmailError);
    this.$root.$on('checkout:clear-validate', this.clearGuestEmailError);
  },
  methods: {
    validateGuestEmailForCheckout() {
      if (!this.email || !this.email.trim() || !this.isValidEmail(this.email)) {
        this.setGuestEmailError(this.$t('Please enter a valid email address.'));
        return false;
      }

      this.guestEmailValidationError = '';
      return !this.guestEmailAvailabilityError;
    },
    updateGuestEmail() {
      this.$store.commit('checkout/setGuestInfo', {
        data: { ...this.guestInfo, email: this.email },
      });

      if (this.isValidEmail(this.email)) {
        this.checkEmail();
        this.guestEmailValidationError = '';
        this.$store.commit('checkout/setCheckoutError', { data: null });
      } else if (this.email === '') {
        this.guestEmailValidationError = '';
        this.guestEmailAvailabilityError = '';
        this.showLoginLink = false;
        this.$store.commit('checkout/setCheckoutError', { data: null });
      } else {
        this.guestEmailValidationError = this.$t(
          'Please enter a valid email address.'
        );
        this.guestEmailAvailabilityError = '';
        this.showLoginLink = false;
      }
    },
    openLogin() {
      if (this.$refs.loginDrawer) {
        this.$refs.loginDrawer.toggleMenu = true;
      }
    },
    async handleNewsletterChange(val) {
      if (val && this.isValidEmail(this.email)) {
        try {
          const visitor = await this.$store.dispatch(
            'shop/localisation/fetchVisitor'
          );
          const ip = visitor.ip;
          const languageIsoCode = this.$i18n.locale;
          const currencyIsoCode =
            this.$store.state.shop.localisation.appCurrencyIsoCode;

          await this.customerService.subscribeNewsletter({
            email: this.email,
            ip,
            languageIsoCode,
            currencyIsoCode,
          });
        } catch (e) {
          console.error('Newsletter subscription failed', e);
        }
      }
    },
    checkEmail() {
      if (this._checkTimer) clearTimeout(this._checkTimer);
      this._checkTimer = setTimeout(async () => {
        try {
          await this.customerService.checkEmailAvailability(this.email);
          this.guestEmailAvailabilityError = '';
          this.showLoginLink = false;
        } catch (e) {
          if (e.response && e.response.status === 409) {
            const msg = this.$t(
              'This email is already registered. Please log in or use a different email to create a new account.'
            );
            this.guestEmailAvailabilityError = msg;
            this.showLoginLink = true;
          } else {
            console.error(e);
          }
        }
      }, 500);
    },
    isValidEmail(val) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val || '');
    },
    setGuestEmailError(msg) {
      this.guestEmailValidationError =
        msg || this.$t('Please enter a valid email address.');
    },
    clearGuestEmailError() {
      this.guestEmailValidationError = '';
    },
    async preRegisterGuest() {
      if (this.guestPreRegistered || this.guestPreRegistering || this.isGuestAlreadyRegistered) return;
      this.guestPreRegistering = true;

      try {
        await this.customerService.registerGuest({
          account: {
            email: this.email,
            firstname: this.shippingAddress.Firstname,
            lastname: this.shippingAddress.Lastname,
            addressInfo: this.shippingAddress,
          },
          languageIsoCode: this.$i18n.locale,
          currencyIsoCode: this.$store.state.cart?.Currency?.IsoCode,
        });

        // If separate invoice address, add it
        if (!this.sameAsDelivery && this.invoiceAddress?.Firstname) {
          await this.addressService.add({
            ...this.invoiceAddress,
            IsDelivery: false,
            IsInvoice: true,
          });
        }

        await this.addressService.fetchAddresses();
        localStorage.removeItem('checkout_guest_data');
        this.guestPreRegistered = true;
        console.log('[Guest] Pre-registered in background — pay button will be instant');
      } catch (e) {
        // Silently fail — ensureAddressesSaved will retry on pay click if needed
        console.warn('[Guest] Background pre-registration failed, will retry on pay:', e?.message);
      } finally {
        this.guestPreRegistering = false;
      }
    },
    saveToStorage() {
      if (this._saveTimer) clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => {
        const data = {
          guestInfo: this.guestInfo,
          shippingAddress: this.shippingAddress,
          invoiceAddress: this.invoiceAddress,
          sameAsDelivery: this.sameAsDelivery,
        };
        localStorage.setItem('checkout_guest_data', JSON.stringify(data));
      }, 500);
    },
    loadFromStorage() {
      const stored = localStorage.getItem('checkout_guest_data');
      this.showemail = true;
      if (!stored) return;
      try {
        const data = JSON.parse(stored);
        const commit = (type, val) =>
          val && this.$store.commit(`checkout/${type}`, { data: val });

        if (data.guestInfo) {
          this.email = data.guestInfo.email || '';
          commit('setGuestInfo', data.guestInfo);
        }
        commit('setShippingAddress', data.shippingAddress);
        commit('setInvoiceAddress', data.invoiceAddress);
        if (data.sameAsDelivery !== undefined) {
          this.$store.commit('checkout/setSameAsDelivery', {
            data: data.sameAsDelivery,
          });
        }
      } catch (e) {
        console.error('Failed to parse guest data', e);
      }
    },
  },
  watch: {
    'guestInfo.email'(newVal) {
      if (newVal && newVal !== this.email) {
        this.email = newVal;
      }
    },
    '$store.state.checkout': {
      handler() {
        this.saveToStorage();
      },
      deep: true,
    },
    isGuestFormReady(ready) {
      if (ready && !this.guestPreRegistered && !this.guestPreRegistering) {
        this.preRegisterGuest();
      }
    },
  },
  beforeDestroy() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    this.$root.$off('checkout:validate', this.validateGuestEmailForCheckout);
    this.$root.$off('checkout:guest-email-error', this.setGuestEmailError);
    this.$root.$off('checkout:clear-validate', this.clearGuestEmailError);
  },
  components: {
    Checkbox,
  },
};
